import { Ionicons } from '@expo/vector-icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dimensions,
  FlatList,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useKeyboardHeight } from '@/lib/useKeyboardHeight';

const WINDOW_HEIGHT = Dimensions.get('window').height;
const SHEET_HEIGHT = WINDOW_HEIGHT * 0.8;

type Option = { id: number; name: string };

type Props = {
  label: string;
  value: number | null;
  options: Option[];
  onChange: (id: number | null) => void;
  placeholder?: string;
};

/** Πεδίο-επιλογή με αναζήτηση, για λίστες με πολλές τιμές (νομοί, κλάδοι κτλ). */
export function SearchablePickerField({ label, value, options, onChange, placeholder }: Props) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const insets = useSafeAreaInsets();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const keyboardHeight = useKeyboardHeight();

  const selected = options.find((o) => o.id === value) ?? null;

  // Όταν σηκώνεται το πληκτρολόγιο, το SHEET_HEIGHT (80% οθόνης) + το ύψος του
  // πληκτρολογίου μπορεί να ξεπεράσει το 100% — μαζεύουμε το ύψος του sheet ώστε
  // να μη βγαίνει η μπάρα αναζήτησης έξω από το πάνω μέρος της οθόνης.
  const sheetHeight = keyboardHeight > 0
    ? Math.min(SHEET_HEIGHT, WINDOW_HEIGHT - keyboardHeight - insets.top - spacing.md)
    : SHEET_HEIGHT;

  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase('el');
    if (!q) return options;
    return options.filter((o) => o.name.toLocaleLowerCase('el').includes(q));
  }, [options, query]);

  function openPicker() {
    setQuery('');
    setOpen(true);
  }

  function pick(id: number | null) {
    onChange(id);
    setOpen(false);
  }

  return (
    <View style={styles.wrap}>
      <Text style={styles.label}>{label}</Text>
      <Pressable style={styles.field} onPress={openPicker} accessibilityRole="button">
        <Text style={[styles.fieldText, !selected && styles.fieldPlaceholder]} numberOfLines={1}>
          {selected?.name ?? placeholder ?? t('common.select')}
        </Text>
        <Ionicons name="chevron-down" size={18} color={colors.textMuted} />
      </Pressable>

      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable style={styles.overlay} onPress={() => setOpen(false)} accessibilityRole="button">
          <Pressable
            style={[styles.sheet, { height: sheetHeight, paddingBottom: insets.bottom + spacing.md, marginBottom: keyboardHeight }]}
            onPress={() => {}}
          >
            <View style={styles.handle} />
            <View style={styles.sheetHeader}>
              <Text style={styles.sheetTitle}>{label}</Text>
              <Pressable onPress={() => setOpen(false)} hitSlop={12} accessibilityRole="button">
                <Ionicons name="close" size={22} color={colors.textMuted} />
              </Pressable>
            </View>

            <View style={styles.searchRow}>
              <Ionicons name="search-outline" size={16} color={colors.textMuted} />
              <TextInput
                style={styles.searchInput}
                value={query}
                onChangeText={setQuery}
                placeholder={t('common.searchPlaceholder')}
                placeholderTextColor={colors.textMuted}
                autoFocus
                autoCapitalize="none"
                autoCorrect={false}
              />
              {!!query && (
                <Pressable onPress={() => setQuery('')} hitSlop={8} accessibilityRole="button">
                  <Ionicons name="close-circle" size={16} color={colors.textMuted} />
                </Pressable>
              )}
            </View>

            <FlatList
              data={filtered}
              keyExtractor={(item) => String(item.id)}
              style={styles.list}
              keyboardShouldPersistTaps="handled"
              ListEmptyComponent={<Text style={styles.emptyText}>{t('common.noResults')}</Text>}
              renderItem={({ item }) => (
                <Pressable
                  style={[styles.optionRow, value === item.id && styles.optionRowActive]}
                  onPress={() => pick(item.id)}
                  accessibilityRole="button"
                >
                  <Text style={[styles.optionText, value === item.id && styles.optionTextActive]}>{item.name}</Text>
                  {value === item.id && <Ionicons name="checkmark" size={18} color={colors.primary} />}
                </Pressable>
              )}
            />
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    wrap: { gap: spacing.xs },
    label: { color: colors.textMuted, fontSize: 13, fontWeight: '600' },
    field: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: colors.surfaceAlt,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      paddingHorizontal: spacing.md,
      height: 48,
    },
    fieldText: { color: colors.text, fontSize: fontSize.md, flexShrink: 1 },
    fieldPlaceholder: { color: colors.textMuted },
    overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'flex-end' },
    sheet: {
      backgroundColor: colors.surface,
      borderTopLeftRadius: radius.xl,
      borderTopRightRadius: radius.xl,
      padding: spacing.lg,
      gap: spacing.sm,
    },
    handle: {
      width: 40, height: 4, borderRadius: 2,
      backgroundColor: colors.border, alignSelf: 'center', marginBottom: spacing.xs,
    },
    sheetHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    sheetTitle: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
    searchRow: {
      flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
      backgroundColor: colors.surfaceAlt, borderRadius: radius.md,
      borderWidth: 1, borderColor: colors.border,
      paddingHorizontal: spacing.md, height: 44,
    },
    searchInput: { flex: 1, color: colors.text, fontSize: fontSize.md, height: '100%' },
    list: { flex: 1, marginTop: spacing.xs },
    emptyText: { color: colors.textMuted, fontSize: fontSize.sm, textAlign: 'center', paddingVertical: spacing.lg },
    optionRow: {
      flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
      paddingVertical: spacing.sm + 2, paddingHorizontal: spacing.xs,
      borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.border,
    },
    optionRowActive: {},
    optionText: { color: colors.text, fontSize: fontSize.md, flexShrink: 1 },
    optionTextActive: { color: colors.primary, fontWeight: '700' },
  });
}
